// Zeitangaben und Zeiträume der Aktivitäten-Listen, rein und ohne React (mit node --test prüfbar, deshalb ohne Import
// aus anderen Dateien). Standard: Europe/Berlin und Wochenbeginn Montag wie der Rest der Oberfläche (werkzeuge/zeit.ts);
// Einstellungen › Organisation kann beides ändern, `zeitEinstellen` übernimmt die Wahl (daten/meta.tsx ruft es nach dem Laden).

const WOCHENTAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const formatFuer = (zone: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

let teileFormat = formatFuer("Europe/Berlin");
let wochenAnfangSonntag = false;

/** Zeitzone und Wochenbeginn der Organisation übernehmen. Ungültige oder fehlende Werte lassen den Standard stehen. */
export function zeitEinstellen(org: { zeitzone?: string | null; wochenbeginn?: string | null }): void {
  if (org.zeitzone) {
    try {
      teileFormat = formatFuer(org.zeitzone);
    } catch {
      teileFormat = formatFuer("Europe/Berlin");
    }
  }
  wochenAnfangSonntag = org.wochenbeginn === "sonntag";
}

const zwei = (n: number) => String(n).padStart(2, "0");

export function gueltig(iso: string | null | undefined): iso is string {
  return !!iso && !Number.isNaN(new Date(iso).getTime());
}

/** Datum (YYYY-MM-DD) und Uhrzeit (HH:mm) eines Zeitpunkts in Berlin. */
export function berlinTeile(iso: string): { datum: string; zeit: string } {
  const p: Record<string, string> = {};
  for (const x of teileFormat.formatToParts(new Date(iso))) p[x.type] = x.value;
  return { datum: `${p.year}-${p.month}-${p.day}`, zeit: `${zwei(+p.hour % 24)}:${p.minute}` };
}

export function tagPlus(datum: string, n: number): string {
  const [j, m, t] = datum.slice(0, 10).split("-").map(Number);
  const d = new Date(Date.UTC(j, m - 1, t + n));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

/** 0 = Sonntag … 6 = Samstag. */
export function wochentag(datum: string): number {
  const [j, m, t] = datum.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(j, m - 1, t)).getUTCDay();
}

/** Tage von a nach b (b - a). */
export function tageZwischen(a: string, b: string): number {
  const [j1, m1, t1] = a.slice(0, 10).split("-").map(Number);
  const [j2, m2, t2] = b.slice(0, 10).split("-").map(Number);
  return Math.round((Date.UTC(j2, m2 - 1, t2) - Date.UTC(j1, m1 - 1, t1)) / 86400000);
}

/** YYYY-MM-DD -> 20.10.2026 */
export function datumText(datum: string | null | undefined): string {
  if (!datum) return "";
  const [j, m, t] = datum.slice(0, 10).split("-");
  return j && m && t ? `${t}.${m}.${j}` : datum;
}

/** Heutiges Datum in Berlin (YYYY-MM-DD) zu einem Zeitpunkt; `jetzt` nur für Tests. */
export function heuteTag(jetzt: Date = new Date()): string {
  return berlinTeile(jetzt.toISOString()).datum;
}

/** „Heute 09:39“, „Gestern 17:04“, „Mo 12.10. 09:39“ (dieses Jahr), „12.10.2025 09:39“ (anderes Jahr). Leer bei ungültigem Wert. */
export function datumKurz(iso: string | null | undefined, jetzt: Date = new Date()): string {
  if (!gueltig(iso)) return "";
  const { datum, zeit } = berlinTeile(iso);
  const heute = heuteTag(jetzt);
  const diff = tageZwischen(heute, datum);
  if (diff === 0) return `Heute ${zeit}`;
  if (diff === -1) return `Gestern ${zeit}`;
  const [j, m, t] = datum.split("-");
  if (j === heute.slice(0, 4)) return `${WOCHENTAGE[wochentag(datum)]} ${t}.${m}. ${zeit}`;
  return `${t}.${m}.${j} ${zeit}`;
}

export type Zeitraum = "alle" | "heute" | "gestern" | "woche" | "7tage" | "monat" | "30tage" | "eigen";

export const ZEITRAEUME: { id: Zeitraum; label: string }[] = [
  { id: "alle", label: "Alle Zeiten" },
  { id: "heute", label: "Heute" },
  { id: "gestern", label: "Gestern" },
  { id: "woche", label: "Diese Woche" },
  { id: "7tage", label: "Letzte 7 Tage" },
  { id: "monat", label: "Dieser Monat" },
  { id: "30tage", label: "Letzte 30 Tage" },
  { id: "eigen", label: "Zeitraum wählen …" },
];

export function istZeitraum(wert: string | null | undefined): wert is Zeitraum {
  return !!wert && ZEITRAEUME.some((z) => z.id === wert);
}

/** Erster Tag der Woche von `tag` (YYYY-MM-DD): Montag, bei Wochenbeginn Sonntag in der Organisation der Sonntag. */
export function wochenbeginn(tag: string): string {
  const wt = wochentag(tag); // 0 = Sonntag
  if (wochenAnfangSonntag) return tagPlus(tag, -wt);
  return tagPlus(tag, wt === 0 ? -6 : 1 - wt);
}

export function monatsbeginn(tag: string): string {
  return `${tag.slice(0, 8)}01`;
}

export function naechsterMonat(tag: string): string {
  const [j, m] = tag.split("-").map(Number);
  return m === 12 ? `${j + 1}-01-01` : `${j}-${String(m + 1).padStart(2, "0")}-01`;
}

/** Grenzen für die Abfrage: `von` einschließlich, `bis` ausschließlich (der Tag danach), beide als Berliner Datum. */
export function zeitraumGrenzen(
  zeitraum: Zeitraum,
  heute: string,
  eigenVon?: string | null,
  eigenBis?: string | null,
): { von?: string; bis?: string } {
  switch (zeitraum) {
    case "heute":
      return { von: heute, bis: tagPlus(heute, 1) };
    case "gestern":
      return { von: tagPlus(heute, -1), bis: heute };
    case "woche": {
      const anfang = wochenbeginn(heute);
      return { von: anfang, bis: tagPlus(anfang, 7) };
    }
    case "7tage":
      return { von: tagPlus(heute, -6), bis: tagPlus(heute, 1) };
    case "monat":
      return { von: monatsbeginn(heute), bis: naechsterMonat(heute) };
    case "30tage":
      return { von: tagPlus(heute, -29), bis: tagPlus(heute, 1) };
    case "eigen": {
      const grenzen: { von?: string; bis?: string } = {};
      if (eigenVon && /^\d{4}-\d{2}-\d{2}$/.test(eigenVon)) grenzen.von = eigenVon;
      if (eigenBis && /^\d{4}-\d{2}-\d{2}$/.test(eigenBis)) grenzen.bis = tagPlus(eigenBis, 1);
      return grenzen;
    }
    default:
      return {};
  }
}

/** Kurztext des gewählten Zeitraums für den Filterknopf („Letzte 7 Tage“, „12.10.–14.10.2026“, „ab 12.10.2026“). */
export function zeitraumText(zeitraum: Zeitraum, eigenVon?: string | null, eigenBis?: string | null): string {
  if (zeitraum !== "eigen") return ZEITRAEUME.find((z) => z.id === zeitraum)?.label ?? "Alle Zeiten";
  if (eigenVon && eigenBis) return eigenVon === eigenBis ? datumText(eigenVon) : `${datumText(eigenVon)} – ${datumText(eigenBis)}`;
  if (eigenVon) return `ab ${datumText(eigenVon)}`;
  if (eigenBis) return `bis ${datumText(eigenBis)}`;
  return "Zeitraum wählen …";
}

/** Dauer eines Gesprächs für die Liste: „–“ bei null, „0:00“ bei 0 Sekunden, „2:35“, „1:02:03“. */
export function dauerKurz(sekunden: number | null | undefined): string {
  if (sekunden === null || sekunden === undefined || !Number.isFinite(sekunden)) return "";
  const s = Math.max(0, Math.floor(sekunden));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h ? `${h}:${zwei(m)}:${zwei(r)}` : `${m}:${zwei(r)}`;
}
